import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { 
  CubeIcon, 
  PlusIcon, 
  PhotoIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronDownIcon,
  ArrowsUpDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  TagIcon,
  ArrowPathRoundedSquareIcon,
  BuildingStorefrontIcon,
  InformationCircleIcon,
  CalculatorIcon,
  BanknotesIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import { productsAPI, categoriesAPI } from '../../config/api';
import { fetchCategories } from '../../store/slices/categoriesSlice';
import { getImageUrl } from '../../config/api';
import ImageCropper from '../../components/common/ImageCropper';

const LabProducts = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { categories } = useSelector((state) => state.categories);
  
  // States
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid'); // grid or table
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    type: 'sale',
    quantity: 1,
    unit: 'piece',
    price: '',
    currency: 'SAR',
    condition: 'new',
    brand: '',
    model: '',
    manufacturerCountry: '',
    specifications: [],
    tags: [],
    // حقول القيمة الحقيقية للتبادل
    actualValue: '',
    actualValueNotes: '',
    exchangePreferences: {
      preferredCategories: [],
      preferredProducts: [],
      notes: ''
    }
  });

  // Fetch products
  const fetchMyProducts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterType !== 'all') params.type = filterType;
      if (filterStatus !== 'all' && filterStatus !== 'pending' && filterStatus !== 'active' && filterStatus !== 'inactive') {
        // Don't send invalid status values
      } else if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      
      const response = await productsAPI.getMyProducts(params);
      setProducts(response.data.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t('products.fetchError'));
      }
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Safely get localized category name
  const getCategoryName = (category) => {
    if (!category) return t('labProducts.uncategorized');
    const currentLang = i18n.language;
    
    if (typeof category === 'string') {
      const cat = categories.find(c => c._id === category);
      return cat?.name?.[currentLang] || cat?.name?.ar || cat?.name || t('labProducts.uncategorized');
    }
    return category.name?.[currentLang] || category.name?.ar || category.name || t('labProducts.uncategorized');
  };

  // Get unit label with translation
  const getUnitLabel = (unit) => {
    if (!unit) return t('products.units.piece');
    
    // Common unit translations
    const unitTranslations = {
      'piece': t('products.units.piece'),
      'قطعة': t('products.units.piece'),
      'box': t('products.units.box'),
      'علبة': t('products.units.box'),
      'set': t('products.units.set'),
      'طقم': t('products.units.set'),
      'pack': t('products.units.pack'),
      'حزمة': t('products.units.pack')
    };
    
    return unitTranslations[unit] || unit;
  };

  // Fetch categories
  useEffect(() => {
    dispatch(fetchCategories({ isActive: true, limit: 100 }));
    fetchMyProducts();
  }, [dispatch]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchMyProducts();
    }, searchTerm ? 500 : 0);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, filterType, filterStatus]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error(t('labProducts.errors.imageSizeError'));
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImageSrc(reader.result);
        setShowImageCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle crop complete
  const handleCropComplete = (croppedFile, croppedImageUrl) => {
    setUploadedImage(croppedFile);
    setImagePreview(croppedImageUrl);
    setShowImageCropper(false);
    setTempImageSrc(null);
  };

  // Handle crop cancel
  const handleCropCancel = () => {
    setShowImageCropper(false);
    setTempImageSrc(null);
  };

  // Handle product submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name?.trim()) {
      toast.error(t('labProducts.errors.productNameRequired'));
      return;
    }
    
    if (!formData.category) {
      toast.error(t('labProducts.errors.categoryRequired'));
      return;
    }
    
    if (!formData.description?.trim()) {
      toast.error(t('labProducts.errors.descriptionRequired'));
      return;
    }
    
    if (formData.description.trim().length < 10) {
      toast.error(t('labProducts.errors.descriptionMinLength'));
      return;
    }
    
    if (formData.type === 'sale' && (!formData.price || parseFloat(formData.price) <= 0)) {
      toast.error(t('labProducts.errors.priceRequired'));
      return;
    }
    
    if (!formData.quantity || parseInt(formData.quantity) < 1) {
      toast.error(t('labProducts.errors.quantityRequired'));
      return;
    }
    
    if (!uploadedImage && !editingProduct) {
      toast.error(t('labProducts.errors.imageRequired'));
      return;
    }
    
    try {
      // Prepare clean data for submission
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        type: formData.type,
        category: formData.category,
        quantity: parseInt(formData.quantity),
        unit: formData.unit || 'piece',
        condition: formData.condition,
        brand: formData.brand?.trim() || '',
        model: formData.model?.trim() || '',
        manufacturerCountry: formData.manufacturerCountry?.trim() || ''
      };

      // Add price for sale items
      if (formData.type === 'sale') {
        submitData.price = parseFloat(formData.price);
        submitData.currency = formData.currency || 'SAR';
      }

      // Add exchange preferences and actual value for exchange items
      if (formData.type === 'exchange') {
        if (!formData.actualValue || parseFloat(formData.actualValue) <= 0) {
          toast.error(t('labProducts.errors.actualValueRequired'));
          return;
        }
        
        submitData.actualValue = {
          amount: parseFloat(formData.actualValue),
          currency: 'SAR',
          notes: formData.actualValueNotes?.trim() || ''
        };
        
        // console.log('Frontend sending actualValue:', submitData.actualValue);
        
        submitData.exchangePreferences = {
          acceptedCategories: [],
          acceptedConditions: ['new', 'like_new', 'good', 'fair', 'poor'],
          preferredBrands: [],
          notes: formData.exchangePreferences.notes?.trim() || '',
          autoAccept: false
        };
      }

      // Add image if available
      if (uploadedImage) {
        submitData.images = [uploadedImage];
      }
      
      let response;
      if (editingProduct) {
        response = await productsAPI.update(editingProduct._id, submitData);
        toast.success(t('labProducts.errors.productUpdated'));
      } else {
        response = await productsAPI.create(submitData);
        toast.success(t('labProducts.errors.productCreated'));
      }
      
      fetchMyProducts();
      closeModal();
    } catch (error) {
      console.error('Error submitting product:', error);
      
      // Handle validation errors
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        
        if (errorData.errors && Array.isArray(errorData.errors)) {
          // Multiple validation errors
          const errorMessages = errorData.errors.map(err => err.message || err.msg).join(', ');
          toast.error(`${t('labProducts.errors.dataError')}: ${errorMessages}`);
        } else if (errorData.error) {
          toast.error(errorData.error);
        } else if (errorData.message) {
          toast.error(errorData.message);
        } else {
          toast.error(t('labProducts.errors.checkData'));
        }
        
        // Log full error for debugging
        console.error('Validation Error Details:', errorData);
      } else {
        toast.error(error.response?.data?.message || error.message || t('labProducts.errors.saveError'));
      }
    }
  };

  // Close modal and reset form
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      type: 'sale',
      quantity: 1,
      unit: 'piece',
      price: '',
      currency: 'SAR',
      condition: 'new',
      brand: '',
      model: '',
      manufacturerCountry: '',
      specifications: [],
      tags: [],
      // إعادة تعيين حقول القيمة الحقيقية
      actualValue: '',
      actualValueNotes: '',
      exchangePreferences: {
        preferredCategories: [],
        preferredProducts: [],
        notes: ''
      }
    });
    setImagePreview(null);
    setUploadedImage(null);
    setShowImageCropper(false);
    setTempImageSrc(null);
  };

  // Handle edit product
  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      category: product.category._id,
      type: product.type,
      quantity: product.quantity,
      unit: product.unit,
      price: product.price || '',
      currency: product.currency || 'SAR',
      condition: product.condition,
      brand: product.brand || '',
      model: product.model || '',
      manufacturerCountry: product.manufacturerCountry || '',
      specifications: product.specifications || [],
      tags: product.tags || [],
      // إضافة القيمة الحقيقية للتحرير
      actualValue: product.actualValue?.amount || '',
      actualValueNotes: product.actualValue?.notes || '',
      exchangePreferences: product.exchangePreferences || {
        preferredCategories: [],
        preferredProducts: [],
        notes: ''
      }
    });
    if (product.images && product.images.length > 0) {
      setImagePreview(getImageUrl(product.images[0].url));
    }
    setIsModalOpen(true);
  };

  // Handle delete product
  const handleDelete = async (productId) => {
    if (!window.confirm(t('labProducts.confirmDeleteProduct'))) {
      return;
    }
    
    try {
      await productsAPI.delete(productId);
      toast.success(t('labProducts.errors.productDeleted'));
      fetchMyProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(error.response?.data?.message || t('labProducts.errors.deleteError'));
    }
  };

  // Get status badge
  const getStatusBadge = (product) => {
    const statusConfig = {
      pending: { icon: ClockIcon, text: t('labProducts.productStatus.pending'), color: 'bg-yellow-100 text-yellow-800' },
      active: { icon: CheckCircleIcon, text: t('common.active'), color: 'bg-green-100 text-green-800' },
      inactive: { icon: XCircleIcon, text: t('common.inactive'), color: 'bg-gray-100 text-gray-800' },
      rejected: { icon: ExclamationTriangleIcon, text: t('labProducts.productStatus.rejected'), color: 'bg-red-100 text-red-800' },
      sold: { icon: CheckCircleIcon, text: t('labProducts.productStatus.sold'), color: 'bg-blue-100 text-blue-800' },
      exchanged: { icon: ArrowPathRoundedSquareIcon, text: t('labProducts.productStatus.exchanged'), color: 'bg-purple-100 text-purple-800' },
      draft: { icon: DocumentTextIcon, text: t('labProducts.productStatus.draft'), color: 'bg-gray-100 text-gray-800' }
    };
    
    const config = statusConfig[product.status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 icon-text" />
        {config.text}
      </span>
    );
  };

  // Get type badge
  const getTypeBadge = (type) => {
    const typeConfig = {
      sale: { icon: CurrencyDollarIcon, text: t('products.forSale'), color: 'bg-blue-100 text-blue-800' },
      exchange: { icon: ArrowPathRoundedSquareIcon, text: t('products.forExchange'), color: 'bg-purple-100 text-purple-800' },
      asset: { icon: BuildingStorefrontIcon, text: t('lab.assets'), color: 'bg-gray-100 text-gray-800' }
    };
    
    const config = typeConfig[type] || typeConfig.sale;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 icon-text" />
        {config.text}
      </span>
    );
  };

  // Render grid view
  const renderGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {products.map((product) => (
        <motion.div
          key={product._id}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
        >
          {/* Product Image */}
          <div className="relative h-48 bg-gray-100">
            {product.images && product.images.length > 0 ? (
              <img
                src={getImageUrl(product.images[0].url)}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <PhotoIcon className="h-16 w-16 text-gray-400" />
              </div>
            )}
            <div className="absolute top-2 right-2 space-y-2">
              {getStatusBadge(product)}
              {getTypeBadge(product.type)}
            </div>
          </div>

          {/* Product Info */}
          <div className="p-4">
            <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">{product.name}</h3>
            <p className="text-sm text-gray-500 mb-2 line-clamp-2">{product.description}</p>
            
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-gray-500">
                {getCategoryName(product.category)}
              </span>
              <span className="text-xs text-gray-500">
                {t('productDetail.quantity')}: {product.quantity} {getUnitLabel(product.unit)}
              </span>
            </div>

            {product.type === 'sale' && (
              <div className="text-lg font-bold text-blue-600 mb-3">
                {product.price} {product.currency}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-3 border-t">
              <button
                onClick={() => handleEdit(product)}
                className="flex items-center text-blue-600 hover:text-blue-700 transition-colors"
              >
                <PencilIcon className="h-4 w-4 icon-text" />
                <span className="text-sm">{t('common.edit')}</span>
              </button>
              <button
                onClick={() => window.open(`/products/${product.slug}`, '_blank')}
                className="flex items-center text-gray-600 hover:text-gray-700 transition-colors"
              >
                <EyeIcon className="h-4 w-4 icon-text" />
                <span className="text-sm">{t('common.view')}</span>
              </button>
              <button
                onClick={() => handleDelete(product._id)}
                className="flex items-center text-red-600 hover:text-red-700 transition-colors"
              >
                <TrashIcon className="h-4 w-4 icon-text" />
                <span className="text-sm">{t('common.delete')}</span>
              </button>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  // Render table view
  const renderTableView = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 table-header text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('products.product')}
              </th>
              <th className="px-6 py-3 table-header text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('lab.category')}
              </th>
              <th className="px-6 py-3 table-header text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('lab.type')}
              </th>
              <th className="px-6 py-3 table-header text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('productDetail.price')}
              </th>
              <th className="px-6 py-3 table-header text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('productDetail.quantity')}
              </th>
              <th className="px-6 py-3 table-header text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('lab.status')}
              </th>
              <th className="px-6 py-3 table-header text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('lab.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {products.map((product) => (
              <tr key={product._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      {product.images && product.images.length > 0 ? (
                        <img
                          className="h-10 w-10 rounded-lg object-cover"
                          src={getImageUrl(product.images[0].url)}
                          alt={product.name}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                          <PhotoIcon className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="icon-text">
                      <div className="text-sm font-medium text-gray-900">{product.name}</div>
                      <div className="text-sm text-gray-500 line-clamp-1">{product.description}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-900">{getCategoryName(product.category)}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getTypeBadge(product.type)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {product.type === 'sale' ? (
                    <span className="text-sm font-medium text-gray-900">
                      {product.price} {product.currency}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-900">
                    {product.quantity} {getUnitLabel(product.unit)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(product)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <button
                      onClick={() => handleEdit(product)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => window.open(`/products/${product.slug}`, '_blank')}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(product._id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Render modal
  const renderModal = () => (
    <AnimatePresence>
      {isModalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-br from-brand-blue via-brand-blue to-brand-blueSecondary text-white p-6 rounded-t-xl relative overflow-hidden">
              <div className="absolute inset-0 bg-white opacity-5">
                <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                  <pattern id="pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="20" cy="20" r="1" fill="currentColor" />
                  </pattern>
                  <rect width="100%" height="100%" fill="url(#pattern)" />
                </svg>
              </div>
              <div className="relative flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold flex items-center">
                    {editingProduct ? (
                      <>
                        <PencilIcon className="h-7 w-7 icon-text" />
                        {t('labProducts.editProduct')}
                      </>
                    ) : (
                      <>
                        <PlusIcon className="h-7 w-7 icon-text" />
                        {t('lab.addNewProduct')}
                      </>
                    )}
                  </h2>
                  <p className="text-blue-100 text-sm mt-1">
                    {editingProduct ? t('labProducts.updateProductInfo') : t('labProducts.addProductToPlatform')}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Progress Steps */}
                <div className="md:col-span-2 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        1
                      </div>
                      <span className="mr-2 text-sm font-medium text-gray-700">{t('lab.basicInfo')}</span>
                    </div>
                    <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gray-300 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        2
                      </div>
                      <span className="mr-2 text-sm font-medium text-gray-400">{t('labProducts.additionalDetails')}</span>
                    </div>
                  </div>
                </div>

                {/* Category Selection */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('labProducts.selectProductCategory')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                      required
                    >
                      <option value="">{t('labProducts.selectAppropriateCategory')}</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name?.[i18n.language] || category.name?.ar || category.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDownIcon className="absolute dropdown-icon top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t('labProducts.selectCategoryDesc')}</p>
                </div>

                {/* Product Name */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('labProducts.productName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t('labProducts.productNameExample')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Product Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('labProducts.productDescription')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder={t('labProducts.productDescriptionPlaceholder')}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Product Image */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t('labProducts.productImage')} {!editingProduct && <span className="text-red-500">*</span>}
                  </label>
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200">
                                          <div className="flex items-start gap-6">
                      {imagePreview ? (
                        <div className="relative group">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="h-40 w-40 object-cover rounded-xl shadow-lg border-2 border-blue-100"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-40 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  if (tempImageSrc || uploadedImage) {
                                    setTempImageSrc(imagePreview);
                                    setShowImageCropper(true);
                                  }
                                }}
                                className="bg-blue-500 text-white rounded-full p-2 hover:bg-blue-600 transform hover:scale-110 transition-all"
                                title="تعديل الصورة"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setImagePreview(null);
                                  setUploadedImage(null);
                                }}
                                className="bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transform hover:scale-110 transition-all"
                                title="حذف الصورة"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <label className="h-40 w-40 border-2 border-dashed border-blue-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all group">
                          <PhotoIcon className="h-14 w-14 text-blue-400 group-hover:text-blue-600 transition-colors" />
                          <span className="text-xs text-blue-600 mt-2 font-medium">{t('labProducts.clickToSelectImage')}</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-800 mb-2">{t('labProducts.imageGuidelines')}:</h4>
                        <ul className="space-y-1 text-xs text-gray-600">
                          <li className="flex items-start">
                            <CheckCircleIconSolid className="h-4 w-4 text-green-500 icon-text mt-0.5 flex-shrink-0" />
                            {t('labProducts.useHighQualityImage')}
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIconSolid className="h-4 w-4 text-green-500 icon-text mt-0.5 flex-shrink-0" />
                            {t('labProducts.maxSize5MB')}
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIconSolid className="h-4 w-4 text-green-500 icon-text mt-0.5 flex-shrink-0" />
                            {t('labProducts.supportedFormats')}
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIconSolid className="h-4 w-4 text-green-500 icon-text mt-0.5 flex-shrink-0" />
                            سيتم فتح أداة الاقتصاص لتحديد المنطقة المرغوبة
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIconSolid className="h-4 w-4 text-green-500 icon-text mt-0.5 flex-shrink-0" />
                            يمكنك تعديل الصورة لاحقاً بالضغط على زر التحرير
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product Type */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                                         {t('labProducts.productType')} <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <label className={`relative flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.type === 'sale' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="type"
                        value="sale"
                        checked={formData.type === 'sale'}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <CurrencyDollarIcon className={`h-8 w-8 mx-auto mb-2 ${
                          formData.type === 'sale' ? 'text-blue-600' : 'text-gray-400'
                        }`} />
                        <span className={`text-sm font-medium ${
                          formData.type === 'sale' ? 'text-blue-900' : 'text-gray-700'
                                                 }`}>{t('products.forSale')}</span>
                      </div>
                      {formData.type === 'sale' && (
                        <div className="absolute top-2 left-2">
                          <CheckCircleIconSolid className="h-5 w-5 text-blue-600" />
                        </div>
                      )}
                    </label>
                    
                    <label className={`relative flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.type === 'exchange' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="type"
                        value="exchange"
                        checked={formData.type === 'exchange'}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <ArrowPathRoundedSquareIcon className={`h-8 w-8 mx-auto mb-2 ${
                          formData.type === 'exchange' ? 'text-purple-600' : 'text-gray-400'
                        }`} />
                        <span className={`text-sm font-medium ${
                          formData.type === 'exchange' ? 'text-purple-900' : 'text-gray-700'
                                                 }`}>{t('products.forExchange')}</span>
                      </div>
                      {formData.type === 'exchange' && (
                        <div className="absolute top-2 left-2">
                          <CheckCircleIconSolid className="h-5 w-5 text-purple-600" />
                        </div>
                      )}
                    </label>
                    
                    <label className={`relative flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.type === 'asset' ? 'border-gray-600 bg-gray-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="type"
                        value="asset"
                        checked={formData.type === 'asset'}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div className="text-center">
                        <BuildingStorefrontIcon className={`h-8 w-8 mx-auto mb-2 ${
                          formData.type === 'asset' ? 'text-gray-700' : 'text-gray-400'
                        }`} />
                        <span className={`text-sm font-medium ${
                          formData.type === 'asset' ? 'text-gray-900' : 'text-gray-700'
                                                 }`}>{t('lab.assets')}</span>
                      </div>
                      {formData.type === 'asset' && (
                        <div className="absolute top-2 left-2">
                          <CheckCircleIconSolid className="h-5 w-5 text-gray-700" />
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                {/* Price (only for sale) */}
                {formData.type === 'sale' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('productDetail.price')} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required={formData.type === 'sale'}
                      />
                      <select
                        name="currency"
                        value={formData.currency}
                        onChange={handleInputChange}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="SAR">{t('labProducts.currency.sar')}</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* القيمة الحقيقية (للتبادل فقط) */}
                {formData.type === 'exchange' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('productDetail.actualValueLabel')} <span className="text-red-500">*</span>
                    </label>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-3">
                      <div className="flex items-start">
                        <InformationCircleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="text-sm text-yellow-800">
                          <p className="font-medium mb-1">⚠️ {t('common.importantNote', 'Important Note')}:</p>
                          <p>{t('labProducts.actualValueWarning', 'You must set the actual price of the product accurately to avoid rejection by administration. This value will be used to calculate exchange fees.')}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        name="actualValue"
                        value={formData.actualValue}
                        onChange={handleInputChange}
                        placeholder={t('labProducts.actualValuePlaceholder')}
                        min="1"
                        step="0.01"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        required={formData.type === 'exchange'}
                      />
                      <div className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-600 font-medium">
                        {t('productDetail.riyal')}
                      </div>
                    </div>
                    <textarea
                      name="actualValueNotes"
                      value={formData.actualValueNotes}
                      onChange={handleInputChange}
                      placeholder={t('labProducts.valueNotesPlaceholder')}
                      rows="2"
                      className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    />
                  </div>
                )}

                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                         {t('labProducts.availableQuantity')} <span className="text-red-500">*</span>
                  </label>
                  <div className="flex space-x-2 space-x-reverse">
                    <input
                      type="number"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      min="1"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <div className="w-24 px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 flex items-center justify-center text-gray-600 font-medium">
                      {t('labProducts.piece')}
                    </div>
                  </div>
                </div>

                {/* Condition */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                         {t('labProducts.productCondition')}
                  </label>
                  <select
                    name="condition"
                    value={formData.condition}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                                         <option value="new">{t('products.condition.new')}</option>
                     <option value="like_new">{t('products.condition.like_new')}</option>
                     <option value="good">{t('products.condition.good')}</option>
                     <option value="fair">{t('products.condition.fair')}</option>
                     <option value="poor">{t('products.condition.poor')}</option>
                  </select>
                </div>

                {/* Brand */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                         {t('productDetail.specifications.brand')}
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                                         placeholder={t('labProducts.brandExample')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Model */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                         {t('productDetail.specifications.model')}
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                                         placeholder={t('labProducts.modelExample')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                         {t('productDetail.specifications.country')}
                  </label>
                  <input
                    type="text"
                    name="manufacturerCountry"
                    value={formData.manufacturerCountry}
                    onChange={handleInputChange}
                                         placeholder={t('labProducts.countryExample')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>



                {/* Exchange Preferences (only for exchange) */}
                {formData.type === 'exchange' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('labProducts.exchangeNotes')}
                    </label>
                    <textarea
                      name="exchangePreferences.notes"
                      value={formData.exchangePreferences.notes}
                      onChange={handleInputChange}
                      placeholder={t('labProducts.exchangeNotesPlaceholder')}
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {/* Fee Calculator Section */}
                <div className="md:col-span-2">
                  {formData.type === 'sale' && formData.price && parseFloat(formData.price) > 0 ? (
                    <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl p-6 border border-blue-200">
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary rounded-lg">
                          <CalculatorIcon className="w-5 h-5 text-white" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900 mr-3">حاسبة الأرباح والرسوم</h4>
                      </div>
                      
                      {(() => {
                        const price = parseFloat(formData.price) || 0;
                        const quantity = parseInt(formData.quantity) || 1;
                        const subtotal = price * quantity;
                        
                        // حساب الرسوم (افتراض المختبر عادي - سيتم تحديثه من الخادم لاحقاً)
                        const isPremium = false; // سيتم جلبه من بيانات المستخدم
                        const platformCommission = isPremium ? 0 : subtotal * 0.18; // 18%
                        const paymentGatewayFees = (subtotal * 0.025) + 1; // 2.5% + 1 ريال
                        const netAmount = subtotal - platformCommission - paymentGatewayFees;
                        const tax = subtotal * 0.15; // 15% يدفعها المشتري
                        const totalToBuyer = subtotal + tax;
                        
                        return (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* عمود البائع */}
                              <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                                  <BanknotesIcon className="w-4 h-4 mr-2 text-green-600" />
                                  ما ستحصل عليه
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">السعر الإجمالي:</span>
                                    <span className="font-medium text-green-600">{subtotal.toLocaleString('ar-SA')} ريال</span>
                                  </div>
                                  <div className="flex justify-between text-red-600">
                                    <span>عمولة المنصة ({isPremium ? '0' : '18'}%):</span>
                                    <span>-{platformCommission.toLocaleString('ar-SA')} ريال</span>
                                  </div>
                                  <div className="flex justify-between text-red-600">
                                    <span>رسوم بوابة الدفع:</span>
                                    <span>-{paymentGatewayFees.toLocaleString('ar-SA')} ريال</span>
                                  </div>
                                  <div className="border-t pt-2 flex justify-between font-bold text-lg">
                                    <span className="text-gray-900">صافي الربح:</span>
                                    <span className="text-green-600">{netAmount.toLocaleString('ar-SA')} ريال</span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* عمود المشتري */}
                              <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                                  <CurrencyDollarIcon className="w-4 h-4 mr-2 text-blue-600" />
                                  ما سيدفعه المشتري
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">سعر المنتج:</span>
                                    <span className="font-medium">{subtotal.toLocaleString('ar-SA')} ريال</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">الشحن:</span>
                                    <span className="text-green-600 font-medium">مجاني</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">ضريبة القيمة المضافة (15%):</span>
                                    <span className="font-medium">{tax.toLocaleString('ar-SA')} ريال</span>
                                  </div>
                                  <div className="border-t pt-2 flex justify-between font-bold text-lg">
                                    <span className="text-gray-900">المجموع:</span>
                                    <span className="text-blue-600">{totalToBuyer.toLocaleString('ar-SA')} ريال</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {!isPremium && (
                              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
                                <div className="flex items-start">
                                  <SparklesIcon className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                                  <div className="text-sm text-yellow-800">
                                    <p className="font-medium mb-1">💡 نصيحة: وفر على الرسوم!</p>
                                    <p>اشترك في <strong>بديل بلس</strong> ووفر {platformCommission.toLocaleString('ar-SA')} ريال في هذا المنتج. اشتراك سنوي بـ 2,760 ريال ووفر عمولة المنصة نهائياً!</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ) : formData.type === 'exchange' ? (
                    <div className="bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 rounded-xl p-6 border border-purple-200">
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg">
                          <ArrowPathRoundedSquareIcon className="w-5 h-5 text-white" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900 mr-3">رسوم التبادل</h4>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <h5 className="font-medium text-gray-900 mb-3">شرائح رسوم التبادل</h5>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-right py-2 font-medium text-gray-700">القيمة الإجمالية</th>
                                  <th className="text-right py-2 font-medium text-gray-700">رسوم التبادل</th>
                                </tr>
                              </thead>
                              <tbody className="space-y-1">
                                <tr className="border-b">
                                  <td className="py-2 text-gray-600">أقل من 1,000 ريال</td>
                                  <td className="py-2 font-medium text-purple-600">150 ريال</td>
                                </tr>
                                <tr className="border-b">
                                  <td className="py-2 text-gray-600">من 1,000 إلى 5,000 ريال</td>
                                  <td className="py-2 font-medium text-purple-600">300 ريال</td>
                                </tr>
                                <tr className="border-b">
                                  <td className="py-2 text-gray-600">من 5,000 إلى 10,000 ريال</td>
                                  <td className="py-2 font-medium text-purple-600">900 ريال</td>
                                </tr>
                                <tr>
                                  <td className="py-2 text-gray-600">أكثر من 10,000 ريال</td>
                                  <td className="py-2 font-medium text-purple-600">1,500 ريال</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                          <div className="flex items-start">
                            <InformationCircleIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                            <div className="text-sm text-blue-800">
                              <p className="font-medium mb-2">كيف يتم حساب رسوم التبادل؟</p>
                              <ul className="space-y-1 list-disc pr-4">
                                <li>يتم جمع القيمة الحقيقية لمنتجك + القيمة الحقيقية للمنتج المطلوب</li>
                                <li>يتم تحديد الشريحة المناسبة حسب الجدول أعلاه</li>
                                <li>يدفع المختبر الذي يقبل العرض رسوم التبادل</li>
                                <li>الرسوم ثابتة بغض النظر عن نوع الاشتراك</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                        
                        {formData.actualValue && parseFloat(formData.actualValue) > 0 && (
                          <div className="bg-white rounded-lg p-4 border border-gray-200">
                            <h5 className="font-medium text-gray-900 mb-2">مثال بناءً على قيمة منتجك</h5>
                            <p className="text-sm text-gray-600 mb-2">
                              قيمة منتجك: <span className="font-medium text-purple-600">{parseFloat(formData.actualValue).toLocaleString('ar-SA')} ريال</span>
                            </p>
                            <p className="text-sm text-gray-600">
                              إذا تم تبادله مع منتج بقيمة مماثلة، ستكون القيمة الإجمالية: <span className="font-medium">{(parseFloat(formData.actualValue) * 2).toLocaleString('ar-SA')} ريال</span>
                            </p>
                            <p className="text-sm text-purple-600 font-medium mt-1">
                              الرسوم المتوقعة: {(() => {
                                const total = parseFloat(formData.actualValue) * 2;
                                if (total < 1000) return '150 ريال';
                                if (total <= 5000) return '300 ريال';
                                if (total <= 10000) return '900 ريال';
                                return '1,500 ريال';
                              })()}
                            </p>
                          </div>
                        )}
                        
                        {/* Badeel Plus Exchange Benefits */}
                        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
                          <div className="flex items-start">
                            <SparklesIcon className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                            <div className="text-sm text-yellow-800">
                              <p className="font-medium mb-1">🌟 ميزة حصرية لمشتركي بديل بلس!</p>
                              <div className="space-y-1">
                                <p>• احصل على <strong>10 تبادلات مجانية</strong> سنوياً بدون أي رسوم</p>
                                <p>• وفر على رسوم التبادل التي قد تصل إلى 1,500 ريال لكل تبادل</p>
                                <p>• إجمالي التوفير المحتمل: <strong>حتى 15,000 ريال سنوياً</strong></p>
                                <p className="text-yellow-700 font-medium mt-2">
                                  💡 اشتراك بديل بلس: 2,760 ريال فقط سنوياً ووفر آلاف الريالات!
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end gap-4 mt-6 pt-6 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {editingProduct ? t('labProducts.saveChanges') : t('labProducts.addProduct')}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <CubeIcon className="h-8 w-8 icon-text text-brand-blue" />
                {t('lab.products')}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {t('labProducts.manageLabProducts')}
              </p>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
                          <PlusIcon className="h-5 w-5 icon-text" />
            {t('lab.addNewProduct')}
            </button>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute dropdown-icon top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('labProducts.searchProducts')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Filter by Type */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('products.allTypes')}</option>
            <option value="sale">{t('products.forSale')}</option>
            <option value="exchange">{t('products.forExchange')}</option>
            <option value="asset">{t('lab.assets')}</option>
          </select>

          {/* Filter by Status */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('lab.allStatuses')}</option>
            <option value="pending">{t('lab.productStatus.pending')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="inactive">{t('common.inactive')}</option>
          </select>

          {/* Sort */}
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [sort, order] = e.target.value.split('-');
              setSortBy(sort);
              setSortOrder(order);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="createdAt-desc">{t('products.newest')}</option>
            <option value="createdAt-asc">{t('products.oldest')}</option>
            <option value="price-asc">{t('products.priceLowHigh')}</option>
            <option value="price-desc">{t('products.priceHighLow')}</option>
            <option value="name-asc">{t('products.alphabetical')}</option>
            <option value="name-desc">{t('products.alphabetical')}</option>
          </select>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <div className="text-sm text-gray-500">
            {t('products.showingResults', 'عرض {{from}} منتج', { from: products.length, total: products.length })}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded ${viewMode === 'table' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Products Display */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : products.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center"
        >
          <CubeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('lab.noProducts')}</h3>
          <p className="text-gray-500 mb-6">{t('labProducts.startAddingProducts')}</p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5 icon-text" />
            {t('lab.addNewProduct')}
          </button>
        </motion.div>
      ) : viewMode === 'grid' ? (
        renderGridView()
      ) : (
        renderTableView()
      )}

      {/* Add Product Modal */}
      {renderModal()}

      {/* Image Cropper Modal */}
      {showImageCropper && tempImageSrc && (
        <ImageCropper
          imageSrc={tempImageSrc}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={1} // مربع 1:1 للمنتجات
          minWidth={200}
          minHeight={200}
        />
      )}
    </div>
  );
};

export default LabProducts;
